import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Prop, Watch } from '@stencil/core';
import { IconName } from '../../shared/icons';
import { getCachedResource } from '../../utils/services/resource-cache.service';
import { getIconSvgUrl } from '../../utils/get-icon';

/**
 * Icons are visual representations of ideas, objects and actions. The majority of our icons are available in two sizes,
 * 16px and 24px, that should be used accordingly for optimal legibility purposes.
 *
 * @slot - Optional contents (SVGs) placed within the component
 */
@Component({
  tag: 'wb-icon',
  styleUrl: 'icon.scss',
  shadow: true,
})
export class IconComponent implements ComponentInterface {
  @Element()
  public el!: HTMLElement;

  /** Name of the icon to use */
  @Prop({ reflect: true, mutable: true })
  public name?: IconName;

  /** Path to a SVG file */
  @Prop({ reflect: true, mutable: true })
  public src?: string;

  /** If present, flips the icon horizontally when `dir` is `"rtl"`. */
  @Prop({ reflect: true })
  public flipRtl: boolean = false;

  /** emitted when icon can not be loaded due to network error */
  @Event()
  public wberror: EventEmitter<Error>;

  @Watch('name')
  public onNameChange(): void {
    this.updateSvg(getIconSvgUrl(this.name));
  }

  @Watch('src')
  public onSrcChange(): void {
    if (this.src && this.src.endsWith('.svg')) {
      this.updateSvg(this.src);
    } else {
      this.clearIcon();
    }
  }

  public componentDidLoad(): void {
    if (this.name) {
      this.onNameChange();
    } else if (this.src) {
      this.onSrcChange();
    }
  }

  private updateSvg(resource: string): void {
    getCachedResource(resource) //
      .then(this.setSvgContent)
      .catch(this.handleIconLoadError);
  }

  private handleIconLoadError = (error: Error) => {
    this.wberror.emit(error);
    this.clearIcon();
  };

  private clearIcon = (): void => {
    this.setSvgContent('');
  };

  private setSvgContent = (svgContent: string): void => {
    const slotSpan = this.el.shadowRoot.querySelector('span');
    if (slotSpan) {
      slotSpan.innerHTML = svgContent;
    }
  };

  public render(): JSX.Element {
    return (
      <Host class="wb-icon" aria-hidden="true">
        <slot>
          <span />
        </slot>
      </Host>
    );
  }
}
